import React from 'react';

import { Button, HeroLayout, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { capitalizeTopic } from 'src/app/shared/utils/topic';

import { AiAccordion } from './components/AiAccordion';
import { ImageWithAttribution } from './components/ImageWithAttribution';
import { TopicStats } from './components/TopicStats';
import { usePageHeading } from './hooks/usePageHeading';

export const PageHeading: React.FC = () => {
	const {
		data: { description, name, hasTopicPhoto, trackSAP, onAccordionToggle, structuredData },
	} = usePageHeading();
	const { translate } = useI18n();

	const capitalizedName = capitalizeTopic(name);

	return (
		<div className={cn('md:mt-16', { 'mt-4': !hasTopicPhoto })}>
			<HeroLayout
				media={hasTopicPhoto && <ImageWithAttribution imgAlt={name} />}
				reverseMd
				variant="1/3"
				fullWidthMediaSm
				className="bg-background-raised"
			>
				<div className="bg-background-raised relative shrink-0 grow-0 basis-1/2 rounded-b-xl md:h-full md:rounded-tl-xl md:rounded-br-none">
					<div className="flex h-full flex-col items-start justify-between gap-6 p-6 md:p-12">
						<div>
							<Text variant="body" className="text-typography-weak hidden md:block">
								<Translate value="corgi.topic_page.page_heading.topic" />
							</Text>

							<Text
								as="h1"
								variant="heading"
								size="x-large"
								className="text-typography-strong mt-2 mb-6"
								data-qa="topic-heading"
							>
								{capitalizedName}
							</Text>

							<TopicStats />
						</div>

						<Button
							as="a"
							href="/start-a-petition/intro?source_location=topics_page"
							variant="primary"
							size="small"
							onClick={trackSAP}
							className="max-md:w-full"
							data-qa="header-sap-button"
						>
							<Translate value="corgi.topic_page.page_heading.start_button" />
						</Button>
					</div>
				</div>
			</HeroLayout>

			{/* Only show description when structuredData is not available */}
			{!structuredData && description && (
				<AiAccordion
					content={description}
					dataQa="topic-accordion"
					onToggle={(expanded) => onAccordionToggle(expanded, 'topic_learn_more_expand')}
					title={translate('corgi.topic_page.page_heading.accordion_title', { name })}
				/>
			)}

			{structuredData &&
				structuredData.map((item, index) => (
					<AiAccordion
						key={item.id}
						dataQa={`topic-structured-data-accordion-${index}`}
						content={item.content}
						onToggle={(expanded) => onAccordionToggle(expanded, `topic_structured_data_expand-${index}`)}
						title={item.title}
					/>
				))}
		</div>
	);
};
